// ============================================================================
// NotificationsGate — bloqueia acesso até o user ativar notificações
// ============================================================================
// Quando ativa:
//   - PWA standalone (instalado na tela inicial)
//   - User logado COM professionalId (admin sem prof passa direto)
//   - Push suportado
//   - Ainda não inscrito
//   - Não dispensou recentemente (snooze 7d caso o user negue / pule)
// → Renderiza tela cheia "Ativar notificações"
// → Após ativar (ou snooze) → renderiza children normalmente
//
// Sem condição satisfeita → passa direto pra children (gate transparente).
// ============================================================================

import React, { useEffect, useState } from 'react';
import { Bell, BellRing, Check, ArrowRight, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useData } from '../contexts/DataContext.jsx';
import {
  isPushSupported, getPermission, isSubscribed, subscribeToPush,
} from '../utils/pushNotifications.js';

const LEGACY_SNOOZE_KEY = 'fk_notif_gate_snooze_until';
// Limpa snooze legado (versão antiga deixava o gate sumir por 7 dias)
try { localStorage.removeItem(LEGACY_SNOOZE_KEY); } catch {}

function isStandalone() {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  if (window.navigator?.standalone === true) return true;
  return false;
}

export default function NotificationsGate({ children }) {
  const { user } = useAuth();
  const { professionals = [] } = useData();
  const [resolved, setResolved] = useState(false); // já passou pelo gate
  const [checking, setChecking] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const standalone = isStandalone();
  const supported = isPushSupported();
  const myProf = user?.professionalId
    ? professionals.find(p => p.id === user.professionalId)
    : null;
  // Chave de destinatário do push: perfil se houver, senão o id do usuário.
  // O admin master NÃO tem professionalId — sem isto, o app pulava o registro
  // e o celular dele nunca se inscrevia (não recebia push nenhum). (16/07/2026)
  const recipientId = user?.professionalId || user?.id || null;

  // Decide se mostra o gate
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Critérios pra NÃO bloquear (passa direto)
      if (!user) { setResolved(true); setChecking(false); return; }
      if (!recipientId) { setResolved(true); setChecking(false); return; }
      if (!standalone) { setResolved(true); setChecking(false); return; }
      if (!supported) { setResolved(true); setChecking(false); return; }

      // Device já tem subscription E permissão concedida?
      //   → REBIND silencioso pro user atual (no iPhone a subscription é
      //     compartilhada entre logins do mesmo device — sem rebind, push
      //     vai pra quem ativou primeiro).
      //   → Não mostra gate (permissão já tá ok, só atualiza o vínculo).
      // Sem subscription / permissão pendente → mostra o gate.
      try {
        const sub = await isSubscribed();
        if (cancelled) return;
        if (sub && getPermission() === 'granted') {
          // Rebind silencioso (subscribeToPush re-aproveita endpoint existente
          // e faz upsert no /api/push-subscribe com o destinatário atual)
          subscribeToPush(recipientId).catch(() => {});
          setResolved(true);
          setChecking(false);
          return;
        }
      } catch { /* deixa o gate aparecer */ }

      // Permission denied → não tem como reverter no app, mostra gate explicativo
      // (com opção de pular pra não travar)
      setChecking(false);
    })();
    return () => { cancelled = true; };
  }, [user, standalone, supported]);

  const handleActivate = async () => {
    if (!recipientId) return;
    setError('');
    setLoading(true);
    try {
      await subscribeToPush(recipientId);
      setResolved(true);
    } catch (err) {
      setError(err?.message || 'Erro ao ativar notificações');
    } finally {
      setLoading(false);
    }
  };

  // Sem snooze por design: a gente "obriga" todo mundo a ativar pra
  // garantir alcance dos triggers (duelo, conquista, etc). Se iOS bloquear
  // permission no nível de sistema, ainda permite escapar com "Continuar
  // mesmo assim" (mostrado SÓ no caso 'denied' — abaixo).
  const handleSkipDenied = () => setResolved(true);

  if (resolved) return children;
  if (checking) {
    return (
      <div className="min-h-screen bg-[#0F1B2D] flex items-center justify-center">
        <Loader2 size={28} className="text-brand-300 animate-spin" />
      </div>
    );
  }

  const permission = getPermission();
  const denied = permission === 'denied';

  return (
    <div className="min-h-screen bg-[#0F1B2D] flex items-center justify-center px-4 relative overflow-hidden">
      {/* Glows de fundo */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-brand/[0.10] rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-brand-300/[0.05] rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-sm animate-fade-in">
        {/* Ícone hero */}
        <div className="flex justify-center mb-6">
          <div className="relative">
            <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-[#41659E] to-[#29426F] flex items-center justify-center shadow-2xl shadow-brand/20">
              <BellRing size={36} className="text-white" />
            </div>
            <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 border-2 border-[#0F1B2D] animate-pulse" />
          </div>
        </div>

        <div className="bg-[#16243A] border border-[#23344F] rounded-2xl p-7 shadow-2xl">
          <h2 className="text-xl font-bold text-[#E9EEF5] text-center tracking-tight">
            Ativar notificações
          </h2>
          <p className="text-[#8C9CB5] text-sm text-center mt-2 leading-relaxed">
            Pra acompanhar seus duelos, conquistas e desafios em tempo real.
          </p>

          {/* Lista de benefícios */}
          <div className="mt-6 space-y-3">
            {[
              { emoji: '⚔️', text: 'Resultado dos duelos da semana' },
              { emoji: '🏆', text: 'Conquistas desbloqueadas' },
              { emoji: '🎯', text: 'Lembretes do desafio diário' },
              { emoji: '🥇', text: 'Mudanças no ranking' },
            ].map((b, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-brand-300/15 border border-brand-300/20 flex items-center justify-center text-base flex-shrink-0">
                  {b.emoji}
                </span>
                <span className="text-sm text-[#E9EEF5]">{b.text}</span>
              </div>
            ))}
          </div>

          {denied ? (
            <div className="mt-6 bg-red-950/40 border border-red-900/40 rounded-xl p-3 text-xs text-red-300">
              <p className="font-semibold mb-1">Notificações bloqueadas no dispositivo</p>
              <p className="leading-relaxed">
                Vai em <strong>Ajustes → Notificações → Feel King</strong> e libere.
                Depois volta aqui e atualize.
              </p>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleActivate}
              disabled={loading || !myProf?.id}
              className="w-full mt-6 bg-gradient-to-r from-[#41659E] to-[#29426F] text-white font-bold py-3.5 rounded-xl text-sm tracking-wide flex items-center justify-center gap-2 hover:opacity-95 disabled:opacity-50 transition-all"
            >
              {loading ? (
                <><Loader2 size={16} className="animate-spin" /> Ativando...</>
              ) : (
                <><Bell size={16} /> Ativar notificações <ArrowRight size={14} /></>
              )}
            </button>
          )}

          {error && (
            <div className="mt-4 bg-red-950/40 border border-red-900/40 rounded-lg px-3 py-2 text-xs text-red-300">
              {error}
            </div>
          )}

          {denied && (
            <button
              type="button"
              onClick={handleSkipDenied}
              className="w-full mt-3 text-[#5B6C86] text-xs hover:text-[#8C9CB5] transition-colors py-2"
            >
              Continuar mesmo assim
            </button>
          )}
        </div>

        <p className="text-[10px] text-[#34445E] text-center mt-5 tracking-wide">
          Você pode mudar isso nas configurações do dispositivo
        </p>
      </div>
    </div>
  );
}
